const AttributeDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet Attributes — Comprehensive Documentation
      </h2>

      <p className="mb-6">
        The <strong>AxisNet.Attributes</strong> library provides declarative,
        attribute-driven behaviors for authorization, session validation
        bypassing, and Redis cache integration. These attributes are consumed by
        the AxisNet MediatR pipeline behaviors, offering a clean,
        annotation-based approach to controlling access, caching, and session
        policies.
      </p>

      {/* FEATURE FIELD ATTRIBUTE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          FeatureField — AxisNet.Cli Domain → Feature Generator Integration
        </h3>

        <p className="mb-4">
          The <strong>FeatureField Attribute</strong> is used by AxisNet.Cli to
          scan domain class properties and determine{" "}
          <strong>which operations</strong>
          (Create, Update, List, GetById) these properties should participate in
          when generating the Feature layer.
        </p>

        <p className="mb-6">
          This attribute can only be applied at the <strong>Property</strong>{" "}
          level and works in combination with flag values defined in the
          <code>FeatureUsage</code> enum.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Attribute Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[AttributeUsage(AttributeTargets.Property)]
public class FeatureField : Attribute
{
    public FeatureUsage Usage { get; set; }

    public FeatureField(FeatureUsage usage)
    {
        Usage = usage;
    }
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-6 mb-2">
          FeatureUsage — Flag Enum Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[Flags]
public enum FeatureUsage
{
    None        = 0,
    Create      = 1 << 0,
    Update      = 1 << 1,
    Delete      = 1 << 2,
    GetById     = 1 << 3,
    List        = 1 << 4
}`}
        </pre>

        <p className="mt-4 mb-4">
          <code>FeatureUsage</code> is a <strong>[Flags]</strong> enum, which
          means a property can participate in multiple operations
          simultaneously:
        </p>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[FeatureField(FeatureUsage.Create | FeatureUsage.Update)]
public string Name { get; set; }`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-8 mb-3">
          How AxisNet.Cli Uses This Attribute
        </h4>

        <p className="mb-4">
          AxisNet.Cli reads each property and inspects its{" "}
          <strong>Usage</strong>
          flags, then automatically adds the property to the generated Command
          or Response models in the Feature layer.
        </p>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>CreateCommand</strong> → includes properties marked with
            <code>FeatureUsage.Create</code>.
          </li>
          <li>
            <strong>UpdateCommand</strong> → includes properties marked with
            <code>FeatureUsage.Update</code>.
          </li>
          <li>
            <strong>DeleteCommand</strong> → typically includes only the ID
            field.
          </li>
          <li>
            <strong>GetByIdQuery Response</strong> → includes properties marked
            with
            <code>FeatureUsage.GetById</code>.
          </li>
          <li>
            <strong>ListQuery Response</strong> → includes properties marked
            with
            <code>FeatureUsage.List</code>.
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mt-8 mb-2">
          🔧 Example Domain Model
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class Product
{
    public Guid Id { get; set; }

    [FeatureField(FeatureUsage.Create | FeatureUsage.Update)]
    public string Name { get; set; }

    [FeatureField(FeatureUsage.Create | FeatureUsage.Update)]
    public decimal Price { get; set; }

    [FeatureField(FeatureUsage.List | FeatureUsage.GetById)]
    public bool IsActive { get; set; }
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-8 mb-2">
          🧩 AxisNet.Cli Output — Automatically Generated Code
        </h4>

        <p className="mb-3 font-semibold text-emerald-300">CreateCommand.cs</p>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class CreateProductCommand : IRequest<Guid>
{
    public string Name { get; set; }
    public decimal Price { get; set; }
}`}
        </pre>

        <p className="mt-6 mb-3 font-semibold text-emerald-300">
          UpdateCommand.cs
        </p>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class UpdateProductCommand : IRequest<bool>
{
    public Guid Id { get; set; }
    public string Name { get; set; }
    public decimal Price { get; set; }
}`}
        </pre>

        <p className="mt-6 mb-3 font-semibold text-emerald-300">
          GetByIdQueryResponse.cs
        </p>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class GetProductByIdResponse
{
    public Guid Id { get; set; }
    public bool IsActive { get; set; }
}`}
        </pre>

        <p className="mt-6 mb-3 font-semibold text-emerald-300">
          ListQueryResponse.cs
        </p>
        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`public class ProductListItemDto
{
    public Guid Id { get; set; }
    public bool IsActive { get; set; }
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-10 mb-2">
          🎯 Summary — What Problem Does It Solve?
        </h4>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            Defines which properties are used in which generated features.
          </li>
          <li>
            Automatically injects properties into Commands/Responses via
            AxisNet.Cli.
          </li>
          <li>Eliminates manual duplication when creating Feature layers.</li>
          <li>Provides full flexibility through bitwise flag combinations.</li>
        </ul>
      </div>

      {/* AUTHORIZATION ATTRIBUTE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          AuthorizationAttribute — Role & Permission Enforcement
        </h3>

        <p className="mb-4">
          <strong>AuthorizationAttribute</strong> is applied on MediatR request
          classes to declare which Roles and Permissions are required for the
          handler to execute. It is processed by the{" "}
          <code>AuthorizationBehavior&lt;TRequest,TResponse&gt;</code>, which
          validates the authenticated user’s roles and permissions.
        </p>

        <p className="mb-4">
          Both roles and permissions are passed as semicolon-separated strings.
          If only roles are specified, permission validation is skipped. If both
          are specified, the user must have at least one matching role AND at
          least one matching permission.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Attribute Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[AttributeUsage(AttributeTargets.Class | AttributeTargets.Method, AllowMultiple = false)]
public class AuthorizationAttribute : Attribute
{
    public string Roles { get; set; }
    public string? Permissions { get; set; }

    public AuthorizationAttribute(string roles)
    {
        Roles = roles;
        Permissions = null;
    }
    
    public AuthorizationAttribute(string roles, string permissions)
    {
        Roles = roles;
        Permissions = permissions;
    }
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-4 mb-2">
          Handled By
        </h4>
        <p className="mb-2">
          <code>AuthorizationBehavior&lt;TRequest,TResponse&gt;</code>
          Validates:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Authentication state</li>
          <li>Role presence</li>
          <li>Permission presence (when required)</li>
          <li>User’s claims from JWT</li>
        </ul>
      </div>

      {/* BYPASS SESSION VALIDATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          BypassSessionValidationAttribute — Skip Session Checks
        </h3>

        <p className="mb-4">
          <strong>BypassSessionValidationAttribute</strong> disables the session
          validation pipeline for the decorated request. This is critical for
          endpoints such as login, register, refresh-token, or public endpoints
          where a valid session should not be required.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Attribute Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[AttributeUsage(AttributeTargets.Class | AttributeTargets.Method, AllowMultiple = true)]
public class BypassSessionValidationAttribute : Attribute
{
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-4 mb-2">
          Handled By
        </h4>
        <p className="mb-2">
          <code>SessionValidationBehavior&lt;TRequest,TResponse&gt;</code>
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Disables IP-match enforcement</li>
          <li>Skips AccessToken and RefreshToken expiry validation</li>
          <li>Skips session existence checks</li>
        </ul>
      </div>

      {/* CACHEABLE ATTRIBUTE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          CacheableAttribute — Redis Caching (Read Optimization)
        </h3>

        <p className="mb-4">
          <strong>CacheableAttribute</strong> enables automatic response caching
          for requests handled by MediatR. Cached values are stored in Redis and
          retrieved on subsequent calls with the same computed cache key.
        </p>

        <p className="mb-4">
          Developers can use template placeholders inside the cache key, such
          as:
          <code>{" {PageRequest.Page} "}</code>, which are dynamically replaced
          by the pipeline behavior.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Attribute Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[AttributeUsage(AttributeTargets.Class | AttributeTargets.Method, AllowMultiple = true)]
public class CacheableAttribute : Attribute
{
    public string Key { get; }
    public int DurationSeconds { get; }
    
    public CacheableAttribute(string key, int durationSeconds = 60)
    {
        Key = key;
        DurationSeconds = durationSeconds;
    }
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-4 mb-2">
          Handled By
        </h4>
        <p className="mb-2">
          <code>RedisCacheBehavior&lt;TRequest,TResponse&gt;</code>
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Generates cache key (supports nested placeholders)</li>
          <li>Checks Redis for existing data</li>
          <li>Saves MediatR response when not cached</li>
          <li>Returns cached result immediately</li>
        </ul>
      </div>

      {/* CACHE REMOVE ATTRIBUTE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          CacheRemoveAttribute — Remove Cached Entries by Prefix
        </h3>

        <p className="mb-4">
          <strong>CacheRemoveAttribute</strong> clears Redis cache entries that
          match a given prefix. This attribute is used on mutation operations
          (create, update, delete) to ensure read consistency across cached
          data.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">
          Attribute Structure
        </h4>

        <pre className="bg-black/50 p-4 rounded text-sm text-emerald-300">
          {`[AttributeUsage(AttributeTargets.Class | AttributeTargets.Method, AllowMultiple = true)]
public class CacheRemoveAttribute : Attribute
{
    public string Prefix { get; }

    public CacheRemoveAttribute(string prefix)
    {
        Prefix = prefix;
    }
}`}
        </pre>

        <h4 className="text-lg font-semibold text-white mt-4 mb-2">
          Handled By
        </h4>
        <p className="mb-2">
          <code>RedisCacheBehavior&lt;TRequest,TResponse&gt;</code>
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Executes after the handler returns</li>
          <li>Removes all keys starting with the given prefix</li>
          <li>Ensures consistency after write operations</li>
        </ul>
      </div>
    </section>
  );
};

export default AttributeDoc;
